import { FC, useEffect, useMemo, useState } from 'react';
import { useBlocker, useSearchParams } from 'react-router-dom';

import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';

import { CreatedMcpApiKey } from '@/apis/admin-api';
import { Role } from '@/apis/admin-api/types';
import { TrblAddIcon, TrblFileIcon } from '@/components/Icons';
import { PrimaryButton, SecondaryButton } from '@/components/Shared/Buttons';
import { useGetMySDKUser } from '@/hooks';

import { CreateKeySidepanel } from './components/CreateKeySidepanel';
import { CreateServerSidepanel } from './components/CreateServerSidepanel';
import { CreatedKeyDialog } from './components/CreatedKeyDialog';
import { KeysGrid } from './components/KeysGrid';
import { ServerRow, ServersGrid } from './components/ServersGrid';
import { BUILT_IN_SERVERS, getOwnerName, KeyRow, MCP_BASE_URL, MCP_DOCS_URL } from './constants';
import {
  useCreateMcpApiKey,
  useCreateMcpEndpoint,
  useDeleteMcpEndpoint,
  useGetMcpEndpoints,
  useGetMcpSettings,
  useSetMcpEnabled,
  useGetMyMcpApiKeys,
  useGetOrganizationMcpApiKeys,
  useRevokeMcpApiKey,
  useRotateMcpApiKey,
} from './hooks';

import adminStyles from '../AdministrationPage/styles.module.scss';
import styles from './styles.module.scss';

const ListError: FC<{ label: string; onRetry: () => void }> = ({ label, onRetry }) => (
  <div className={styles['list-error']}>
    {label}
    <SecondaryButton label="Retry" width={100} onClick={onRetry} />
  </div>
);

const isActive = (key: KeyRow) => !key.revokedAt && new Date(key.expiresAt) > new Date();

export const McpServersPage: FC = () => {
  const { data: sdkUser, isLoading: isLoadingSdkUser } = useGetMySDKUser();
  const isAdmin = sdkUser?.roles?.some((role) => role === Role.Admin || role === Role.Superuser) ?? false;

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = tabParam === 'keys' || (isAdmin && tabParam === 'organization') ? tabParam : 'servers';
  const ownerFilter = tab === 'organization' ? searchParams.get('user') ?? '' : '';

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  // Id of the server the new key is for, while the create panel is open.
  const [creatingForServer, setCreatingForServer] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<{ key: CreatedMcpApiKey; isRotated: boolean } | null>(null);
  const [revoking, setRevoking] = useState<KeyRow | null>(null);
  const [rotating, setRotating] = useState<KeyRow | null>(null);
  const [isCreatingServer, setIsCreatingServer] = useState(false);
  const [deletingServer, setDeletingServer] = useState<ServerRow | null>(null);
  const [isConfirmingMcpOff, setIsConfirmingMcpOff] = useState(false);

  const myKeysQuery = useGetMyMcpApiKeys();
  const organizationKeysQuery = useGetOrganizationMcpApiKeys(isAdmin);
  const keysQuery = tab === 'organization' ? organizationKeysQuery : myKeysQuery;
  const { data: myKeys } = myKeysQuery;
  const { data: organizationKeys } = organizationKeysQuery;
  const endpointsQuery = useGetMcpEndpoints();
  const settingsQuery = useGetMcpSettings();
  const { mutate: changeMcpEnabled, isPending: isChangingMcpEnabled } = useSetMcpEnabled();
  const mcpEnabled = settingsQuery.data?.mcpEnabled;
  const showServersAndKeys = isAdmin || mcpEnabled !== false;
  const { mutate: createServer, isPending: isCreateServerPending } = useCreateMcpEndpoint();
  const { mutate: deleteServer, isPending: isDeleteServerPending } = useDeleteMcpEndpoint();
  const { mutate: createKey, isPending: isCreatePending, reset: resetCreate } = useCreateMcpApiKey();
  const { mutate: revokeKey, isPending: isRevokePending } = useRevokeMcpApiKey();
  const { mutate: rotateKey, isPending: isRotatePending, reset: resetRotate } = useRotateMcpApiKey();
  const isSecretPending = isCreatePending || isRotatePending;

  const clearCreatedKey = () => {
    setCreatedKey(null);
    resetCreate();
    resetRotate();
  };

  // Stay on the page while a key is being created or rotated, so its one-time secret is shown.
  const blocker = useBlocker(isSecretPending);
  useEffect(() => {
    if (!isSecretPending && blocker.state === 'blocked') blocker.reset();
  }, [isSecretPending, blocker]);

  const servers = useMemo<ServerRow[]>(
    () => [
      ...BUILT_IN_SERVERS.map((server) => ({ ...server, isBuiltIn: true, canDelete: false })),
      ...(endpointsQuery.data ?? []).map((endpoint) => ({
        id: endpoint.id,
        name: endpoint.name,
        url: `${MCP_BASE_URL}${endpoint.path}`,
        scopes: endpoint.scopes,
        createdAt: endpoint.createdAt,
        isBuiltIn: false,
        shared: endpoint.shared,
        canDelete: isAdmin || endpoint.createdBy === sdkUser?.id,
      })),
    ],
    [endpointsQuery.data, isAdmin, sdkUser?.id]
  );
  const firstPath = endpointsQuery.data?.[0]?.path;
  const serverUrlPrefix = firstPath
    ? `${MCP_BASE_URL}${firstPath.slice(0, firstPath.lastIndexOf('/') + 1)}`
    : `${MCP_BASE_URL}/mcp/<organization>/`;

  // Don't show the secret again when the browser restores this page from its back/forward cache.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => event.persisted && clearCreatedKey();
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  });

  const owners = useMemo(() => {
    const byId = new Map<string, string>();
    organizationKeys?.forEach((key) => byId.set(key.userId, getOwnerName(key)));
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [organizationKeys]);

  const rows = useMemo(() => {
    const keys: KeyRow[] = (tab === 'organization' ? organizationKeys : myKeys) ?? [];
    const query = search.trim().toLowerCase();
    return keys.filter(
      (key) =>
        (showInactive || isActive(key)) &&
        (!ownerFilter || key.userId === ownerFilter) &&
        (!query ||
          key.name.toLowerCase().includes(query) ||
          key.keyPrefix.toLowerCase().includes(query) ||
          (key.ownerEmail?.toLowerCase().includes(query) ?? false))
    );
  }, [tab, organizationKeys, myKeys, search, showInactive, ownerFilter]);

  const setTab = (value: string) => setSearchParams(value === 'servers' ? {} : { tab: value });
  const setOwnerFilter = (userId: string) =>
    setSearchParams(userId ? { tab: 'organization', user: userId } : { tab: 'organization' });

  if (isLoadingSdkUser) {
    return (
      <Box component="div" display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress sx={{ color: '#5BF6D1' }} />
      </Box>
    );
  }

  return (
    <div className={styles['page']}>
      <div>
        <div className={styles['header']}>
          <h1>MCP servers</h1>
          <SecondaryButton
            label="Documentation"
            width="fit-content"
            icon={<TrblFileIcon />}
            onClick={() => window.open(MCP_DOCS_URL, '_blank', 'noopener,noreferrer')}
          />
        </div>
        <p className={styles['intro']}>
          An MCP server lets AI assistants such as Claude, ChatGPT, Cursor or VS Code search the SDK documentation and
          your libraries. Use a built-in server, or create one for your organization that only reaches the libraries you
          pick. Clients sign in with their Treble account (OAuth), or use an MCP API key if they can't. The
          documentation explains how to choose and set up each client.
        </p>
      </div>

      {isAdmin && settingsQuery.isError && (
        <ListError label="Couldn't load your organization's MCP setting." onRetry={settingsQuery.refetch} />
      )}
      {(isAdmin || mcpEnabled === false) && (
        <div className={styles['toolbar']}>
          {mcpEnabled === false && (
            <p className={styles['muted']} style={{ margin: 0 }}>
              MCP is turned off for your organization: no AI assistant can connect, with OAuth or an API key.
              {!isAdmin && ' Ask an organization admin to turn it on.'}
            </p>
          )}
          <Box component="div" flex="1" />
          {isAdmin && mcpEnabled !== undefined && (
            <FormControlLabel
              control={
                <Switch
                  checked={mcpEnabled}
                  disabled={isChangingMcpEnabled}
                  onChange={(e) => (e.target.checked ? changeMcpEnabled(true) : setIsConfirmingMcpOff(true))}
                />
              }
              label="Allow AI assistants to connect to Treble (MCP)"
            />
          )}
        </div>
      )}

      {showServersAndKeys && (
        <>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} className={styles['tabs']}>
            <Tab value="servers" label="Servers" />
            <Tab value="keys" label="My API keys" />
            {isAdmin && <Tab value="organization" label="Organization API keys" />}
          </Tabs>

          {tab === 'servers' ? (
            <>
              <div className={styles['toolbar']}>
                <Box component="div" flex="1" />
                {isAdmin && (
                  <PrimaryButton
                    label="Create server"
                    icon={<TrblAddIcon fill="none" />}
                    onClick={() => setIsCreatingServer(true)}
                    width={150}
                  />
                )}
              </div>
              <Box component="div" flex="1 1 auto" minHeight={200}>
                {endpointsQuery.isError ? (
                  <ListError label="Couldn't load your organization's MCP servers." onRetry={endpointsQuery.refetch} />
                ) : (
                  <ServersGrid
                    servers={servers}
                    isLoading={endpointsQuery.isLoading || isDeleteServerPending}
                    onCreateKey={(server) => setCreatingForServer(server.id)}
                    onDelete={setDeletingServer}
                  />
                )}
              </Box>
              <p className={styles['footnote']}>
                Admins create servers and choose whether everyone in the organization can use them, or only admins. Each
                person still signs in as themselves, and only admins or the server's creator can delete it.
              </p>
            </>
          ) : (
            <>
              <p className={styles['intro']}>
                Use an API key for clients that can't sign in with your Treble account (OAuth), such as on a server, in
                a container or in CI. A key acts as you, belongs to one server and gets that server's libraries.
              </p>
              <div className={styles['toolbar']}>
                <TextField
                  size="small"
                  placeholder={
                    tab === 'organization' ? 'Search name, key prefix or owner' : 'Search name or key prefix'
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ width: 280, input: { fontSize: 13 } }}
                />
                {tab === 'organization' && (
                  <TextField
                    select
                    size="small"
                    value={ownerFilter}
                    onChange={(e) => setOwnerFilter(e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    sx={{ width: 220, '.MuiSelect-select': { fontSize: 13 } }}>
                    <MenuItem value="" sx={{ fontSize: 13 }}>
                      All users
                    </MenuItem>
                    {owners.map(([userId, name]) => (
                      <MenuItem key={userId} value={userId} sx={{ fontSize: 13 }}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <FormControlLabel
                  control={
                    <Checkbox size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                  }
                  label="Show revoked and expired"
                  className={styles['muted-label']}
                />
                <Box component="div" flex="1" />
                {tab === 'keys' && (
                  <PrimaryButton
                    label="Create key"
                    icon={<TrblAddIcon fill="none" />}
                    onClick={() => setCreatingForServer(servers[0].id)}
                    width={150}
                  />
                )}
              </div>

              <Box component="div" flex="1 1 auto" minHeight={300}>
                {keysQuery.isError ? (
                  <ListError label="Couldn't load MCP API keys." onRetry={keysQuery.refetch} />
                ) : (
                  <KeysGrid
                    keys={rows}
                    showOwner={tab === 'organization'}
                    isLoading={keysQuery.isLoading || isRevokePending || isRotatePending}
                    canRotate={tab === 'keys'}
                    onRotate={setRotating}
                    onRevoke={setRevoking}
                  />
                )}
              </Box>

              <p className={styles['footnote']}>
                Revoking or rotating a key takes effect within 5 minutes. Keys stop working when their owner loses SDK
                access, is disabled, or moves to another organization, and when their server is deleted.
              </p>
            </>
          )}
        </>
      )}

      {isCreatingServer && (
        <CreateServerSidepanel
          urlPrefix={serverUrlPrefix}
          isSubmitting={isCreateServerPending}
          onCancel={() => setIsCreatingServer(false)}
          onSubmit={(request) => createServer(request, { onSuccess: () => setIsCreatingServer(false) })}
        />
      )}

      {isConfirmingMcpOff && (
        <Dialog
          open
          onClose={() => !isChangingMcpEnabled && setIsConfirmingMcpOff(false)}
          className={adminStyles['confirmation-dialog-container']}>
          <DialogTitle>Turn off MCP</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This disables all MCP servers for everyone in your organization, the built-in ones and your organization's
              own, with OAuth and with API keys. Connected AI assistants lose access within a minute. Servers and keys
              are kept and work again when you turn MCP back on. Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className={adminStyles['dialog-action-buttons']}>
              <SecondaryButton
                label="Cancel"
                disabled={isChangingMcpEnabled}
                onClick={() => setIsConfirmingMcpOff(false)}
                width={150}
              />
              <PrimaryButton
                label="Turn off"
                disabled={isChangingMcpEnabled}
                onClick={() => changeMcpEnabled(false, { onSettled: () => setIsConfirmingMcpOff(false) })}
                width={150}
              />
            </div>
          </DialogActions>
        </Dialog>
      )}

      {deletingServer && (
        <Dialog open onClose={() => setDeletingServer(null)} className={adminStyles['confirmation-dialog-container']}>
          <DialogTitle>Delete MCP server</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete "{deletingServer.name}"? Clients connected to {deletingServer.url} stop working, and its API keys
              are revoked. This can't be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className={adminStyles['dialog-action-buttons']}>
              <SecondaryButton label="Cancel" onClick={() => setDeletingServer(null)} width={150} />
              <PrimaryButton
                label="Delete"
                onClick={() => {
                  deleteServer(deletingServer.id);
                  setDeletingServer(null);
                }}
                width={150}
              />
            </div>
          </DialogActions>
        </Dialog>
      )}

      {creatingForServer && (
        <CreateKeySidepanel
          servers={servers}
          initialServerId={creatingForServer}
          isSubmitting={isCreatePending}
          onCancel={() => setCreatingForServer(null)}
          onSubmit={(request) =>
            createKey(request, {
              onSuccess: (created) => {
                setCreatingForServer(null);
                setCreatedKey({ key: created, isRotated: false });
              },
            })
          }
        />
      )}

      {createdKey && (
        <CreatedKeyDialog createdKey={createdKey.key} isRotated={createdKey.isRotated} onClose={clearCreatedKey} />
      )}

      {rotating && (
        <Dialog open onClose={() => setRotating(null)} className={adminStyles['confirmation-dialog-container']}>
          <DialogTitle>Rotate MCP API key</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Rotate "{rotating.name}"? You get a new key with the same name, server and lifetime. The current key stops
              working within 5 minutes, so update every client that uses it.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className={adminStyles['dialog-action-buttons']}>
              <SecondaryButton label="Cancel" onClick={() => setRotating(null)} width={150} />
              <PrimaryButton
                label="Rotate"
                disabled={isRotatePending}
                onClick={() =>
                  rotateKey(rotating.id, {
                    onSuccess: (rotated) => {
                      setRotating(null);
                      setCreatedKey({ key: rotated, isRotated: true });
                    },
                  })
                }
                width={150}
              />
            </div>
          </DialogActions>
        </Dialog>
      )}

      {revoking && (
        <Dialog open onClose={() => setRevoking(null)} className={adminStyles['confirmation-dialog-container']}>
          <DialogTitle>Revoke MCP API key</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {revoking.ownerEmail && revoking.userId !== sdkUser?.id
                ? `Revoke "${revoking.name}" owned by ${revoking.ownerEmail}? `
                : `Revoke "${revoking.name}"? `}
              Clients using it lose access within 5 minutes. This can't be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className={adminStyles['dialog-action-buttons']}>
              <SecondaryButton label="Cancel" onClick={() => setRevoking(null)} width={150} />
              <PrimaryButton
                label="Revoke"
                onClick={() => {
                  revokeKey(revoking.id);
                  setRevoking(null);
                }}
                width={150}
              />
            </div>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};
